import { DateQuestionModeType, RangeConditionType } from '../dateValidation/types';
import { MAX_OFFSET_ALLOWED_HOURS, MIN_OFFSET_ALLOWED_HOURS } from './constants';

/**
 * Converts days to hours (multiplies by 24).
 * Use this when dateMode is 'date' to convert display value to internal storage format.
 */
export const convertToHoursFromDays = (days: string): string => {
  const daysNumber = Number(days);
  return (daysNumber * 24).toString();
};

/**
 * Converts hours to days (divides by 24).
 * Use this when dateMode is 'date' to convert internal storage format to display value.
 */
export const convertToDaysFromHours = (hours: string): string => {
  return (Number(hours) / 24).toString();
};

/**
 * Converts an internal offset in hours (stored format) to a display offset value.
 * - In 'date' mode: converts hours to days.
 * - In 'datetime' or 'time' mode: returns the hours value as-is.
 *
 */
export const convertOffsetHoursToDisplayOffsetValue = (
  hours: string,
  dateMode: DateQuestionModeType,
): string => {
  if (!hours || hours === '') {
    return '';
  }
  return dateMode === 'date' ? convertToDaysFromHours(hours) : hours;
};

/**
 * Converts a display offset value to the internal offset in hours (stored format).
 * - In 'date' mode: converts days to hours.
 * - In 'datetime' or 'time' mode: returns the hours value as-is.
 */
export const convertDisplayOffsetValueToOffsetHours = (
  value: string,
  dateMode: DateQuestionModeType,
): string => {
  if (!value || value === '') {
    return '';
  }
  return dateMode === 'date' ? convertToHoursFromDays(value) : value;
};

export const hasDateValidationErrors = (
  selectedRangeCondition: RangeConditionType,
  dateMode: DateQuestionModeType,
  offsetHours?: string,
  offsetHoursToLeft?: string,
  offsetHoursToRight?: string,
): boolean => {
  if (!selectedRangeCondition) return false;
  if (['onlyToday', 'noFutureDates', 'noPastDates'].includes(selectedRangeCondition)) {
    return false;
  }
  const safeDateMode: DateQuestionModeType = dateMode || 'datetime';
  const minOffsetAllowedHours = MIN_OFFSET_ALLOWED_HOURS[safeDateMode];
  const maxOffsetHoursAllowedHours = MAX_OFFSET_ALLOWED_HOURS[safeDateMode];

  const minOffsetAllowedValue = Number(
    convertOffsetHoursToDisplayOffsetValue(minOffsetAllowedHours.toString(), safeDateMode),
  );
  const maxOffsetAllowedValue =
    maxOffsetHoursAllowedHours !== null
      ? Number(
          convertOffsetHoursToDisplayOffsetValue(
            maxOffsetHoursAllowedHours.toString(),
            safeDateMode,
          ),
        )
      : null;

  if (['between', 'notBetween'].includes(selectedRangeCondition)) {
    const offsetHoursToLeftValue = Number(
      convertOffsetHoursToDisplayOffsetValue(offsetHoursToLeft || '', safeDateMode),
    );
    const offsetHoursToRightValue = Number(
      convertOffsetHoursToDisplayOffsetValue(offsetHoursToRight || '', safeDateMode),
    );

    const isInvalidOffsetToLeftValue =
      isNaN(offsetHoursToLeftValue) ||
      offsetHoursToLeftValue < minOffsetAllowedValue ||
      (maxOffsetAllowedValue !== null && offsetHoursToLeftValue > maxOffsetAllowedValue);
    const isInvalidOffsetToRightValue =
      isNaN(offsetHoursToRightValue) ||
      offsetHoursToRightValue < minOffsetAllowedValue ||
      (maxOffsetAllowedValue !== null && offsetHoursToRightValue > maxOffsetAllowedValue);

    return isInvalidOffsetToLeftValue || isInvalidOffsetToRightValue;
  }

  const offsetValue = Number(
    convertOffsetHoursToDisplayOffsetValue(offsetHours || '', safeDateMode),
  );
  const isInvalidOffsetValue =
    isNaN(offsetValue) ||
    offsetValue < minOffsetAllowedValue ||
    (maxOffsetAllowedValue !== null && offsetValue > maxOffsetAllowedValue);

  return isInvalidOffsetValue;
};
